/**
 * Same rules as hybrid_milp_system/fleet_mapping/validation.py. Keep the two in step.
 * A time t is in a window when start <= t <= end, so windows that touch (09:00 end, 09:00 start) overlap.
 */

const HHMM = /^([01]?\d|2[0-3]):([0-5]\d)$/;

export const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function parseHhmm(value) {
  const match = HHMM.exec(String(value ?? '').trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

export function formatHhmm(minutes) {
  const h = String(Math.floor(minutes / 60)).padStart(2, '0');
  const m = String(minutes % 60).padStart(2, '0');
  return `${h}:${m}`;
}

function overlappingPairs(spans) {
  const ordered = [...spans].sort((a, b) => (a.start - b.start) || (a.end - b.end));
  const pairs = [];
  ordered.forEach((later, i) => {
    for (let j = 0; j < i; j += 1) {
      if (later.start <= ordered[j].end) pairs.push([ordered[j], later]);
    }
  });
  return pairs;
}

/**
 * @param {Array<{fleet_id: string, windows: Array<{driver_id, start, end}>}>} fleets
 * @returns {Array<{fleet_id, window_index, driver_id, code, message}>}
 */
export function validateWeekdayPlan(fleets) {
  const errors = [];
  const spansByFleet = [];
  const spansByDriver = {};

  (fleets || []).forEach((fleet) => {
    const fleetId = String(fleet?.fleet_id || '').trim();
    if (!fleetId) return;
    const spans = [];
    (fleet.windows || []).forEach((w, index) => {
      const driverId = String(w?.driver_id || '').trim();
      const start = parseHhmm(w?.start);
      const end = parseHhmm(w?.end);
      const where = { fleet_id: fleetId, window_index: index, driver_id: driverId || null };
      if (!driverId) errors.push({ ...where, code: 'driver_required', message: 'Pick a driver' });
      if (start === null || end === null) {
        errors.push({ ...where, code: 'bad_time', message: 'Use HH:MM (00:00–23:59)' });
      } else if (start >= end) {
        errors.push({ ...where, code: 'start_not_before_end', message: 'Start must be before end' });
      }
      if (!driverId || start === null || end === null || start >= end) return;
      const span = { start, end, index, fleetId, driverId };
      spans.push(span);
      (spansByDriver[driverId] = spansByDriver[driverId] || []).push(span);
    });
    spansByFleet.push(spans);
  });

  spansByFleet.forEach((spans) => {
    overlappingPairs(spans).forEach(([a, b]) => {
      errors.push({
        fleet_id: b.fleetId,
        window_index: b.index,
        driver_id: b.driverId,
        other_window_index: a.index,
        code: 'fleet_overlap',
        message: `Overlaps ${formatHhmm(a.start)}–${formatHhmm(a.end)} on this fleet`,
      });
    });
  });

  Object.entries(spansByDriver).forEach(([driverId, spans]) => {
    overlappingPairs(spans).forEach(([a, b]) => {
      if (a.fleetId === b.fleetId) return;
      errors.push({
        fleet_id: b.fleetId,
        window_index: b.index,
        driver_id: driverId,
        other_fleet_id: a.fleetId,
        other_window_index: a.index,
        code: 'driver_overlap',
        message: `Driver is already on another fleet ${formatHhmm(a.start)}–${formatHhmm(a.end)}`,
      });
    });
  });

  return errors;
}

/** { "fleetId:index": [messages] } for highlighting rows. */
export function errorsByWindow(errors) {
  const map = {};
  (errors || []).forEach((e) => {
    const key = `${e.fleet_id}:${e.window_index}`;
    (map[key] = map[key] || []).push(e.message);
  });
  return map;
}
